import utils from './utils';
import uuid from 'uuid/v4';
import * as Sentry from '@sentry/browser';
import { WidgetError } from '../WidgetError';
import serverApi from './serverApi';
import { lazyLoaders } from './loading-scheduler';

export const TWO_HOURS_IN_MINUTES = '120';
export const TIME_TO_DISCONNECT_IN_MILLIES = 3 * 60 * 1000;

const shouldDisconnectVisitor = (lastActiveTimestamp, timeTillDisconnect) => {
  const diffMins = utils.diffMinutes(lastActiveTimestamp, new Date());
  return timeTillDisconnect && diffMins >= timeTillDisconnect;
};

const shouldResetTimestamp = (
  firebaseInitialLoading,
  lastDisconnectionTime,
) => {
  if (!firebaseInitialLoading) {
    const idleTime = Date.now() - lastDisconnectionTime;
    return idleTime > TIME_TO_DISCONNECT_IN_MILLIES;
  }
};

class FirebaseChatService {
  timeTillDisconnect;
  visitorInstanceId;
  lastActiveTimestamp;

  private interval;
  private fb?: firebase.app.App;
  private db?: firebase.database.Database;
  private eventPath?: string;
  private presencePath?: string;
  private initialConnection = true;
  private firebaseInitialLoading = true;
  private readonly presenceState = {
    timestamp: '',
    lastSeenTimestamp: '',
    pageName: '',
    location: '',
    deviceType: '',
  };
  private lastDisconnectionTime;
  private correlationId;
  private biToken;
  private visitorId;
  private instanceId;
  private coreChatToken;
  private presenceNGEnabled = false;
  private isPresenceViaChatSdk = false;

  async init({
    authKey,
    options,
    eventPath,
    presencePath,
    visitorInstanceId,
    biToken,
    visitorId,
    instanceId,
    correlationId,
    coreChatToken,
    presenceNGEnabled,
    isPresenceViaChatSdk,
  }) {
    Sentry.addBreadcrumb({ category: 'FireBase', message: 'Initializing' });

    this.eventPath = eventPath;
    this.presencePath = presencePath;
    this.visitorInstanceId = visitorInstanceId;
    this.correlationId = correlationId;
    this.biToken = biToken;
    this.visitorId = visitorId;
    this.instanceId = instanceId;
    this.timeTillDisconnect = TWO_HOURS_IN_MINUTES;
    this.lastActiveTimestamp = new Date();
    this.coreChatToken = coreChatToken;
    this.presenceNGEnabled = presenceNGEnabled;
    this.isPresenceViaChatSdk = isPresenceViaChatSdk;

    if (!this.initialConnection) {
      window.clearInterval(this.interval);
      this.interval = null;
      this.fb = undefined;
    }

    const firebase = require('firebase/app');
    require('firebase/auth');
    require('firebase/database');

    this.fb = firebase.initializeApp(options, uuid());
    const fb = this.fb as firebase.app.App;

    await fb.auth().setPersistence(firebase.auth.Auth.Persistence.NONE);
    try {
      await fb.auth().signInWithCustomToken(authKey);
    } catch (e) {
      throw new WidgetError(
        'signInWithCustomToken of firebase has failed.',
        e,
        { str: authKey },
      );
    }
    this.db = fb.database();
    this.setHeartbeat();
    this.initialConnection = false;
    Sentry.addBreadcrumb({ category: 'FireBase', message: 'Initialized' });
  }

  getLastSeenTimestamp() {
    const firebase = require('firebase/app');
    return firebase.database.ServerValue.TIMESTAMP;
  }

  setLastSeenTimestamp() {
    const firebase = require('firebase/app');

    void this.updatePresence({
      lastSeenTimestamp: firebase.database.ServerValue.TIMESTAMP,
    });
  }

  goOnlineIfOffline() {
    this.lastActiveTimestamp = new Date();
    if (!this.interval) {
      this.db && this.db.goOnline();
      this.setHeartbeat();
      this.setLastSeenTimestamp();
    }
  }

  goOffline() {
    this.db && this.db.goOffline();
  }

  setHeartbeat() {
    this.interval = setInterval(() => {
      if (
        shouldDisconnectVisitor(
          this.lastActiveTimestamp,
          this.timeTillDisconnect,
        )
      ) {
        window.clearInterval(this.interval);
        this.interval = null;
        this.goOffline();
      } else {
        this.setLastSeenTimestamp();
      }
    }, 1000 * 90);
  }

  removeOnDisconnect() {
    return this.db && this.db.ref(this.presencePath).onDisconnect().remove();
  }

  subscribeToEventsAndInfo({ eventCallback, timestamp }) {
    if (!this.db) {
      return;
    }

    const eventsRef = this.db
      .ref(this.eventPath)
      .orderByChild('timestamp')
      .startAt(new Date(timestamp).getTime());
    // Make sure we remove all previous listeners.
    eventsRef.off();
    this.subscribeToInfoEvents();
    eventsRef.on('child_added', (snapshot) => {
      if (snapshot) {
        const val = snapshot.val();
        eventCallback(typeof val === 'string' ? JSON.parse(val) : val);
      }
    });
    return this;
  }

  subscribeToInfoEvents() {
    if (!this.db) {
      return;
    }

    const connectionInfoRef = this.db.ref('.info/connected');
    connectionInfoRef.on('value', async (connectedSnap) => {
      if (connectedSnap && connectedSnap.val() === true) {
        await this.removeOnDisconnect();

        void this.updatePresence(
          shouldResetTimestamp(
            this.firebaseInitialLoading,
            this.lastDisconnectionTime,
          ) // set state with all previous data +? new timestamp
            ? { timestamp: utils.getCurrentTimestamp() }
            : {},
        );

        this.firebaseInitialLoading = false;
      } else {
        this.lastDisconnectionTime = utils.getCurrentDate();
      }
    });
  }

  async updatePresence(newState, updateOnPresenceNG = true) {
    if (window.__IS_BOT__) {
      return;
    }

    Object.assign(this.presenceState, newState);

    if (!this.interval) {
      this.goOnlineIfOffline();
    }

    this.db && this.db.ref(this.presencePath).set(this.presenceState);

    if (
      this.presenceNGEnabled &&
      updateOnPresenceNG &&
      this.presencePath &&
      this.visitorInstanceId
    ) {
      const group = this.presencePath.split('/')[1];
      const [userId, sessionId] = this.visitorInstanceId.split('|');

      if (this.isPresenceViaChatSdk) {
        const lazySdk = await lazyLoaders.interactivity.chatSdk();
        void lazySdk.chatSdk.presence.set(sessionId, group, this.presenceState);
      } else {
        void serverApi.setPresence(
          {
            userId,
            sessionId,
            group,
            status: 'ONLINE',
            data: this.presenceState,
          },
          this.coreChatToken,
          {
            operation: 'UPDATE',
          },
        );
      }
    }

    return this;
  }
}

export default new FirebaseChatService();
